import { Text } from '@daily/shared/components/Text';
import { DailyParticipant } from '@daily-co/daily-js';
import {
  ExtendedDailyParticipant,
  useLocalSessionId,
  useParticipantCounts,
  useParticipantIds,
  useParticipantProperty,
} from '@daily-co/daily-react';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { MAIN_ROOM_IDENTIFIER } from '/components/BreakoutRooms/helpers/constants';
import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { useCallState } from '/contexts/CallProvider';
import { useCallConfig } from '/hooks/useCallConfig';
import { useStyleVariants } from '/hooks/useStyleVariants';
import { HAND_RAISED_USER_DATA_KEY } from '/lib/constants';
import { useIsMobile } from '/lib/state/core';
import { PREBUILT_USER_DATA_KEY } from '/lib/userData';

import { BreakoutRoomHeader } from './BreakoutRoomHeader';
import { ParticipantRow } from './ParticipantRow';
import { PeopleActions } from './PeopleActions';
import { WaitingList } from './WaitingList';

interface StringRow {
  content: string;
  type: 'br-name' | 'participant';
}
interface CountRow {
  content: number;
  type: 'br-presenters' | 'br-viewers';
}
interface SimpleRow {
  type: 'presenters' | 'viewers' | 'main-room';
}
type VirtualRowConfig = StringRow | CountRow | SimpleRow;

export const People: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const [isMobile] = useIsMobile();
  const { iconVariant, textVariant, textVariantSmall } = useStyleVariants();
  const { broadcast, broadcastRole } = useCallConfig();
  const { disableAudio } = useCallState();
  const {
    breakoutHost,
    breakoutMapping,
    breakoutParticipantIds,
    breakoutParticipantsByRoomName,
    breakoutStatus,
    currentBreakoutRoom,
    hasJoinedBreakout,
    mainRoomParticipantIds,
    participantsAskingForHelp,
  } = useBreakoutSessionState();
  const localSessionId = useLocalSessionId();

  const isOwner = useParticipantProperty(localSessionId, 'owner');

  // Order: local, then screen sharers, then hand raisers, then everyone else.
  const sortParticipantIds = useCallback(
    (a: ExtendedDailyParticipant, b: ExtendedDailyParticipant) => {
      // Always put the local user first
      if (a.local || b.local) return 1;

      // Not the local user? Check if they're screen sharing.
      const aScreen = a.screen;
      const bScreen = b.screen;

      // user_name for guests is '' (empty string)
      const aUserName = (a.user_name || t('people.guest')).toLowerCase();
      const bUserName = (b.user_name || t('people.guest')).toLowerCase();

      if (aScreen && bScreen) {
        if (aUserName < bUserName) return -1;
        if (aUserName > bUserName) return 1;
      }

      if (aScreen && !bScreen) {
        return -1;
      }

      if (!aScreen && bScreen) {
        return 1;
      }

      // Not screen sharing? Check if they've raised their hand.
      const aDate =
        a.userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY];
      const bDate =
        b.userData?.[PREBUILT_USER_DATA_KEY]?.[HAND_RAISED_USER_DATA_KEY];

      // Both hands raised? -> compare queue number
      if (aDate && bDate) {
        if (aDate < bDate) return -1;
        if (aDate > bDate) return 1;
        return 0;
      }

      // A has their hand raised?
      if (aDate && !bDate) {
        return -1;
      }

      // B has their hand raised?
      if (!aDate && bDate) {
        return 1;
      }

      // Sort everyone else alphabetically
      if (aUserName < bUserName) return -1;
      if (aUserName > bUserName) return 1;

      return 0;
    },
    [t]
  );

  const peopleRef = useRef<HTMLDivElement>(null);

  const stackGap = isMobile ? 16 : 8;

  const itemHeight = (textVariant === 'base' ? 16 : 24) + stackGap;

  const { present } = useParticipantCounts();
  /**
   * Holds ids of presenters, which means participants with presence data.
   * In broadcasts, we'll limit presenters to owners.
   */
  const presenterIds = useParticipantIds({
    filter: useCallback(
      (p: DailyParticipant) => (broadcast ? p.owner : true),
      [broadcast]
    ),
    sort: sortParticipantIds,
  });

  const screenSharerIds = useParticipantIds({
    filter: 'screen',
  });

  /**
   * Holds ids of viewers (which is only a thing in broadcasts).
   * In regular calls this will always be empty.
   * In regular broadcasts this will be participants with presence data,
   * but without owner rights.
   * In broadcasts with enabled passive participants this will be empty,
   * because passive participants don't appear in daily.participants().
   */
  const viewerIds = useParticipantIds({
    filter: useCallback(
      (p: DailyParticipant) => (broadcast ? !p.owner : false),
      [broadcast]
    ),
    sort: sortParticipantIds,
  });

  /**
   * Holds all elements for virtualized scrolling.
   * 'presenters' and 'viewers' are placeholder values
   * for their respective titles.
   */
  const rows = useMemo(() => {
    const rows: VirtualRowConfig[] = [
      {
        type: 'presenters',
      },
    ];

    if (breakoutStatus === 'ongoing') {
      const baseIds = hasJoinedBreakout
        ? breakoutParticipantIds
        : mainRoomParticipantIds;
      const presenters = presenterIds.filter((p) => baseIds.includes(p));
      const viewers = viewerIds.filter((p) => baseIds.includes(p));

      // Room owner POV
      if (isOwner) {
        // For the room owners, the main room should be on top.
        rows.push({
          type: 'main-room',
        });
        mainRoomParticipantIds.forEach((p) =>
          rows.push({
            type: 'participant',
            content: p,
          })
        );
        Object.values(breakoutMapping).map((room) => {
          rows.push({
            type: 'br-name',
            content: room.name,
          });
          const usersPerRoom = breakoutParticipantsByRoomName(room.name);
          usersPerRoom?.forEach((users) => {
            users.map((id) => {
              rows.push({
                type: 'participant',
                content: id,
              });
            });
          });
        });
      }

      // Participant POV
      if (!isOwner) {
        if (hasJoinedBreakout) {
          // show the participant their breakout room if they've joined the session
          rows.push({
            type: 'br-name',
            content: currentBreakoutRoom.name,
          });
        } else {
          // not in a breakout session? show the participant the main room. they don't get to see the other
          // breakout rooms.
          // The participants in the main room are rendered below, via 'presenters' or 'viewers'
          rows.push({
            type: 'main-room',
          });
        }
        if (presenters.length) {
          if (broadcast)
            rows.push({
              type: 'br-presenters',
              content: presenters.length,
            });
          rows.push(
            ...presenters.map<VirtualRowConfig>((id) => ({
              type: 'participant',
              content: id,
            }))
          );
        }
        if (viewers.length)
          rows.push(
            {
              type: 'br-viewers',
              content: viewers.length,
            },
            ...viewers.map<VirtualRowConfig>((id) => ({
              type: 'participant',
              content: id,
            }))
          );
      }
    } else {
      // Breakouts not ongoing.
      rows.push(
        ...presenterIds.map<VirtualRowConfig>((id) => ({
          type: 'participant',
          content: id,
        }))
      );
      if (viewerIds.length > 0) {
        rows.push(
          {
            type: 'viewers',
          },
          ...viewerIds.map<VirtualRowConfig>((id) => ({
            type: 'participant',
            content: id,
          }))
        );
      }
    }
    return rows;
  }, [
    breakoutMapping,
    breakoutParticipantIds,
    breakoutParticipantsByRoomName,
    breakoutStatus,
    broadcast,
    currentBreakoutRoom.name,
    hasJoinedBreakout,
    isOwner,
    mainRoomParticipantIds,
    presenterIds,
    viewerIds,
  ]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => peopleRef.current,
    estimateSize: useCallback(
      (index) => {
        switch (rows[index].type) {
          case 'presenters':
            return 16;
          case 'viewers':
            return 24 + stackGap + 16;
          default:
            return itemHeight;
        }
      },
      [itemHeight, rows, stackGap]
    ),
    overscan: 5,
  });

  const renderRow = (item: VirtualItem) => {
    const row = rows[item.index];
    switch (row.type) {
      case 'presenters':
        return (
          <Text color="muted" variant={textVariantSmall}>
            {broadcast
              ? t('people.presentersWithCount', {
                  count: presenterIds.length,
                })
              : t('people.withCount', { count: present })}
          </Text>
        );
      case 'viewers':
        return (
          <Text color="muted" variant={textVariantSmall}>
            {t('people.viewersWithCount', {
              count: viewerIds.length,
            })}
          </Text>
        );
      case 'participant':
        return (
          <ParticipantRow
            iconVariant={iconVariant}
            showActions={broadcastRole !== 'attendee'}
            sessionId={row.content}
            showHandRaising={broadcastRole !== 'attendee'}
            showMicMutedState={
              !disableAudio && presenterIds.includes(row.content)
            }
            showIsScreenSharing={
              broadcastRole !== 'attendee' &&
              screenSharerIds.includes(row.content)
            }
            textVariant={textVariant}
          />
        );
      default:
        return null;
    }
  };

  const renderBreakoutRow = (item: VirtualItem) => {
    const row = rows[item.index];

    switch (row.type) {
      case 'presenters':
        return (
          <Text color="muted" variant={textVariantSmall}>
            {t('people.withCount', { count: present })}
          </Text>
        );
      case 'br-presenters':
        return (
          <Text color="muted" variant={textVariantSmall}>
            {t('people.presentersWithCount', {
              count: row.content,
            })}
          </Text>
        );
      case 'br-viewers':
        return (
          <Text color="muted" variant={textVariantSmall}>
            {t('people.viewersWithCount', {
              count: row.content,
            })}
          </Text>
        );
      case 'br-name':
        return <BreakoutRoomHeader name={row.content} />;
      case 'main-room':
        return <BreakoutRoomHeader name={MAIN_ROOM_IDENTIFIER} />;
      case 'participant':
        const showActionsOrHandRaising = broadcastRole !== 'attendee';

        return (
          <ParticipantRow
            iconVariant={iconVariant}
            showActions={showActionsOrHandRaising}
            sessionId={row.content}
            showHandRaising={showActionsOrHandRaising}
            showMicMutedState={
              !disableAudio && presenterIds.includes(row.content)
            }
            showIsAskingForHelp={
              breakoutHost === localSessionId &&
              participantsAskingForHelp.includes(row.content)
            }
            showIsScreenSharing={
              broadcastRole !== 'attendee' &&
              screenSharerIds.includes(row.content)
            }
            textVariant={textVariant}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      ref={peopleRef}
      className="people"
      style={{ '--gap': `${stackGap}px` } as React.CSSProperties}
    >
      <WaitingList />
      <div
        className="list"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((item) => {
          const row = rows[item.index];
          return (
            <div
              key={item.key}
              ref={virtualizer.measureElement}
              data-index={item.index}
              className={classNames('row', {
                noPadding: item.index === 0,
                room: row.type === 'br-name' || row.type === 'main-room',
                title: row.type === 'presenters' || row.type === 'viewers',
              })}
              style={{
                transform: `translateY(${item.start}px)`,
              }}
            >
              {breakoutStatus === 'ongoing'
                ? renderBreakoutRow(item)
                : renderRow(item)}
            </div>
          );
        })}
      </div>
      <PeopleActions />
      <style jsx>{`
        .people {
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          padding: var(--card-spacing);
          position: relative;
        }
        .list {
          display: flex;
          flex-direction: column;
          min-height: calc(100% - 2 * var(--card-spacing));
          position: relative;
        }
        .list .row {
          position: absolute;
          width: 100%;
        }
        .list :global(.participant-row) {
          padding-top: var(--gap);
        }
        .list .title > :global(p) {
          display: block;
          padding-top: calc(24px + var(--gap));
        }
        .list .row.noPadding > :global(*) {
          padding: 0;
        }
      `}</style>
    </div>
  );
};
